/**
 * Column Header Component
 * Reusable component for table column headers with info icon tooltips
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getColumnTooltip } from '../constants/fieldTooltips';

interface ColumnHeaderProps {
  label: string;
  tooltipKey?: string;
  tooltip?: string;
  fontWeight?: number;
}

/**
 * Column header with optional info icon and tooltip
 * 
 * @param label - The column header text
 * @param tooltipKey - Key to lookup tooltip from COLUMN_TOOLTIPS
 * @param tooltip - Direct tooltip text (overrides tooltipKey)
 * @param fontWeight - Font weight for the header text (default: 600)
 */
export const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  label,
  tooltipKey,
  tooltip,
  fontWeight = 600,
}) => {
  const tooltipText = tooltip || (tooltipKey ? getColumnTooltip(tooltipKey) : undefined);

  if (!tooltipText) {
    return (
      <Typography variant="body2" sx={{ fontWeight }}>
        {label}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight }}>
        {label}
      </Typography>
      <Tooltip title={tooltipText} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
};

/**
 * Helper function to create renderHeader for MUI DataGrid columns
 * 
 * Usage:
 *   {
 *     field: 'configKey',
 *     headerName: 'Config Key',
 *     renderHeader: createColumnHeader('Config Key', 'configKey'),
 *   }
 */
export const createColumnHeader = (label: string, tooltipKey?: string, tooltip?: string) => {
  return () => <ColumnHeader label={label} tooltipKey={tooltipKey} tooltip={tooltip} />;
};

